import type { InvitationSummary } from '@debtbot/shared';
import { useT } from '../i18n';
import { formatDay, fullName } from '../lib/format';
import { Button } from './Button';

/**
 * Главная без связей, но с отправленными ссылками. Повторная отправка возможна только для
 * ссылки, которую это устройство само создало в текущей сессии (сервер хранит лишь хеш токена);
 * иначе — новая ссылка, прежняя остаётся действительной.
 */
export function PendingInvites({
  items,
  hasLink,
  pending,
  onResend,
  onInvite,
}: {
  items: InvitationSummary[];
  /** Есть ли у нас ссылка этого приглашения (lib/invite-cache: recallInvite). */
  hasLink: (id: string) => boolean;
  pending: boolean;
  onResend: (inv: InvitationSummary) => void;
  onInvite: () => void;
}) {
  const t = useT();
  const someWithoutLink = items.some((inv) => !hasLink(inv.id));
  return (
    <section data-testid="pending-invites" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t.onboarding.pendingTitle}</h2>
      <ul className="flex flex-col divide-y divide-secondary-bg rounded-2xl bg-secondary-bg px-4">
        {items.map((inv) => {
          const resendable = hasLink(inv.id);
          return (
            <li key={inv.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0 truncate">
                {inv.invitee
                  ? fullName(inv.invitee)
                  : t.onboarding.pendingLink(formatDay(inv.created_at))}
              </div>
              <button
                type="button"
                data-testid={resendable ? 'invite-resend' : 'invite-new-link'}
                disabled={pending}
                className="shrink-0 text-link disabled:opacity-50"
                onClick={() => onResend(inv)}
              >
                {resendable ? t.onboarding.resend : t.onboarding.newLink}
              </button>
            </li>
          );
        })}
      </ul>
      {someWithoutLink && <p className="text-sm text-hint">{t.onboarding.newLinkHint}</p>}
      <Button
        data-testid="invite-another"
        variant="secondary"
        disabled={pending}
        onClick={onInvite}
      >
        ⊕ {t.onboarding.inviteAnother}
      </Button>
    </section>
  );
}
